import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import api from "../api/api";
import { canManagePlanejamentos, getSession } from "../auth/session";
import MagmaSelect from "../components/MagmaSelect";
import "./Management.css";

const statusLabels = {
  1: "Rascunho",
  2: "Em execução",
  3: "Finalizado",
};

export default function PlanejamentosPage() {
  const session = getSession();
  const canEdit = canManagePlanejamentos(session);
  const location = useLocation();
  const preferredWorkspaceId = location.state?.workspaceId
    ? String(location.state.workspaceId)
    : "";

  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedWorkspace = useMemo(
    () => workspaces.find(ws => String(ws.id) === String(workspaceId)),
    [workspaces, workspaceId]
  );

  const loadWorkspaces = async () => {
    const res = await api.get("/workspaces/");
    const list = res.data || [];
    setWorkspaces(list);
    if (list.length > 0 && !workspaceId) {
      const targetId = preferredWorkspaceId || String(list[0].id);
      const exists = list.some(item => String(item.id) === String(targetId));
      setWorkspaceId(exists ? String(targetId) : String(list[0].id));
    }
  };

  const loadPlanejamentos = async id => {
    if (!id) {
      setItems([]);
      return;
    }
    const res = await api.get(`/planejamentos/workspaces/${id}`);
    setItems(res.data || []);
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError("");
        await loadWorkspaces();
      } catch (err) {
        setError(err?.response?.data?.detail || "Falha ao carregar workspaces.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const run = async () => {
      if (!workspaceId) return;
      try {
        setLoading(true);
        setError("");
        await loadPlanejamentos(workspaceId);
      } catch (err) {
        setError(err?.response?.data?.detail || "Falha ao carregar planejamentos.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [workspaceId]);

  const handleDelete = async id => {
    if (!canEdit) return;
    if (!window.confirm("Deseja excluir este planejamento?")) return;

    try {
      setLoading(true);
      setError("");
      await api.delete(`/planejamentos/${id}`);
      await loadPlanejamentos(workspaceId);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao excluir planejamento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mgmt-page">
      <header className="mgmt-header">
        <div>
          <p className="mgmt-kicker">Planejamentos</p>
          <h1 className="mgmt-title">Lista e gestão de planejamentos</h1>
        </div>
      </header>

      <section className="mgmt-card">
        <div className="mgmt-filter-row">
          <div className="mgmt-filter-field">
            <label className="mgmt-label" htmlFor="workspace">Workspace</label>
            <MagmaSelect
              id="workspace"
              className="mgmt-input"
              value={workspaceId}
              onChange={e => setWorkspaceId(e.target.value)}
            >
              {workspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.nome}</option>
              ))}
            </MagmaSelect>
          </div>
          {canEdit ? (
            <Link
              className="mgmt-button mgmt-button-link"
              to="/planejamentos/novo"
              state={{ workspaceId }}
            >
              Criar Planejamento
            </Link>
          ) : null}
        </div>
      </section>

      <section className="mgmt-card">
        <h2 className="mgmt-section-title">Planejamentos {selectedWorkspace ? `- ${selectedWorkspace.nome}` : ""}</h2>
        {loading ? <p className="mgmt-label">Carregando...</p> : null}
        {error ? <p className="mgmt-error">{error}</p> : null}
        <div className="mgmt-table-wrap">
          <table className="mgmt-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.nome}</td>
                  <td>{statusLabels[item.status_id] || `Status ${item.status_id}`}</td>
                  <td className="mgmt-row-actions">
                    <Link
                      className="mgmt-link"
                      to={`/planejamentos/${item.id}`}
                      state={{ planejamento: item, workspaceId }}
                    >
                      Ver
                    </Link>
                    {canEdit ? (
                      <>
                        <Link
                          className="mgmt-link"
                          to={`/planejamentos/${item.id}/editar`}
                          state={{ planejamento: item, workspaceId }}
                        >
                          Editar
                        </Link>
                        <button
                          className="mgmt-link-button danger"
                          type="button"
                          onClick={() => handleDelete(item.id)}
                        >
                          Excluir
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
              {items.length === 0 ? (
                <tr>
                  <td colSpan="4">Nenhum planejamento encontrado para este workspace.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
